import { useEffect, useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * OrbitalRing — Concentric ring SVG with glow effect and center percentage.
 */
function OrbitalRing({ progress = 0, color = 'var(--orbis-blue)', size = 52, animated = true, overLimit = false }) {
  const prefersReducedMotion = useReducedMotion();
  const uid = useMemo(() => `ring-${Math.random().toString(36).slice(2, 6)}`, []);

  const center = size / 2;
  const outerR = (size / 2) - 3;
  const innerR = outerR - 7;
  const outerCircumference = 2 * Math.PI * outerR;
  const innerCircumference = 2 * Math.PI * innerR;
  const outerOffset = outerCircumference - (outerCircumference * Math.min(progress, 100)) / 100;
  const pct = Math.round(Math.min(progress, 100));

  return (
    <div style={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <filter id={`${uid}-glow`}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Outer track */}
        <circle
          cx={center} cy={center} r={outerR}
          fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={2}
        />
        {/* Outer fill with glow */}
        <motion.circle
          cx={center} cy={center} r={outerR}
          fill="none" stroke={color} strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray={outerCircumference}
          initial={{ strokeDashoffset: outerCircumference }}
          animate={{ strokeDashoffset: outerOffset }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.5 }}
          filter={`url(#${uid}-glow)`}
        />
        {/* Inner track */}
        <circle
          cx={center} cy={center} r={innerR}
          fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={1.5}
        />
        {/* Inner decorative arc */}
        <circle
          cx={center} cy={center} r={innerR}
          fill="none" stroke={color} strokeWidth={1} strokeOpacity={0.3}
          strokeLinecap="round"
          strokeDasharray={innerCircumference}
          strokeDashoffset={innerCircumference * 0.7}
        />
      </svg>

      {/* Center label */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontSize: overLimit ? 12 : 9, fontFamily: 'var(--orbis-mono)', fontWeight: 500,
          color: overLimit ? '#F59E0B' : 'var(--orbis-t3)', letterSpacing: '-0.02em',
        }}>
          {overLimit ? '!' : `${pct}%`}
        </span>
      </div>

      {/* Orbiting dot */}
      {animated && !prefersReducedMotion && (
        <div className={`${uid}-orbit`} style={{
          position: 'absolute', top: '50%', left: '50%', width: 0, height: 0,
        }}>
          <div style={{
            position: 'absolute', width: 4, height: 4, borderRadius: '50%',
            background: color, transform: 'translate(-2px, -2px)',
            top: -(outerR + 1),
            boxShadow: `0 0 6px ${color}60`,
          }} />
        </div>
      )}

      <style>{`
        .${uid}-orbit {
          animation: ${uid}-spin 12s linear infinite;
        }
        @keyframes ${uid}-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Premium stat card — celestial command center style.
 * Features: Sora display numbers, orbital ring, trend-colored deltas, hover glow.
 */
export default function StatCard({
  label,
  value,
  context,
  color = 'var(--orbis-blue)',
  index = 0,
  seed = 0,
  icon: Icon,
  contextIcon: ContextIcon,
  progress,
  sparklineData,
}) {
  const prefersReducedMotion = useReducedMotion();
  const [count, setCount] = useState(prefersReducedMotion ? value : 0);
  const [isHovered, setIsHovered] = useState(false);

  // Counter animation
  useEffect(() => {
    if (prefersReducedMotion || typeof value !== 'number') {
      setCount(value);
      return;
    }
    const duration = 900;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(Math.round(eased * value));
      if (t < 1) requestAnimationFrame(animate);
    };
    const timer = setTimeout(() => requestAnimationFrame(animate), (index * 100) + 300);
    return () => clearTimeout(timer);
  }, [value, index, prefersReducedMotion]);

  // Compute ring progress from available data
  const rawProgress = progress !== undefined
    ? progress
    : typeof value === 'number'
      ? Math.min(value * 3, 100)
      : 50;
  const isOverLimit = rawProgress > 100;
  const ringProgress = Math.min(rawProgress, 100);
  const ringColor = isOverLimit ? '#F59E0B' : color;

  // Trend color detection
  const trendColor = context?.startsWith('-') ? '#F59E0B'
    : (context?.startsWith('+') || ContextIcon) ? 'var(--orbis-positive)'
    : 'var(--orbis-t3)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: index * 0.1 + 0.4,
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      whileHover={{ y: -3, transition: { type: 'spring', stiffness: 400, damping: 25 } }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      style={{
        background: 'var(--orbis-s1)',
        border: `0.5px solid ${isHovered ? 'var(--orbis-s3)' : 'var(--orbis-s2)'}`,
        borderRadius: 14,
        padding: '16px 18px 14px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        transition: 'border-color 0.3s ease',
      }}
    >
      {/* Hover glow — top radial */}
      <div style={{
        position: 'absolute', inset: 0,
        opacity: isHovered ? 1 : 0,
        background: `radial-gradient(circle at 30% 0%, ${color}12 0%, transparent 50%)`,
        transition: 'opacity 0.4s ease', pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Row 1: Label (left) + Icon (right, muted) */}
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <p style={{
            fontSize: 11, fontFamily: 'var(--orbis-mono)', fontWeight: 500,
            color: 'var(--orbis-t4)', textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            {label}
          </p>
          {Icon && (
            <motion.div
              animate={{ scale: isHovered ? 1.1 : 1, opacity: isHovered ? 0.6 : 0.3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <Icon style={{ width: 16, height: 16, color: 'var(--orbis-t4)' }} />
            </motion.div>
          )}
        </div>

        {/* Row 2: Value + Context (left) — OrbitalRing (right) */}
        <div className="flex items-end justify-between">
          <div>
            <motion.p
              key={value}
              initial={prefersReducedMotion ? {} : { opacity: 0.6 }}
              animate={{ opacity: 1 }}
              style={{
                fontSize: 26, fontFamily: 'var(--orbis-font-display)', fontWeight: 200,
                color: 'var(--orbis-t1)', lineHeight: 1, marginBottom: 4,
                letterSpacing: '-0.05em',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {typeof value === 'number' ? count : value}
            </motion.p>
            {context && (
              <div className="flex items-center gap-1.5">
                {ContextIcon && (
                  <motion.div animate={{ scale: isHovered ? 1.15 : 1 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
                    <ContextIcon className="w-3 h-3" style={{ color: trendColor }} />
                  </motion.div>
                )}
                <p style={{ fontSize: 12, color: trendColor, fontFamily: 'var(--orbis-font)' }}>
                  {context}
                </p>
              </div>
            )}
          </div>

          {/* Orbital Ring — bottom right (hidden on mobile) */}
          <div className="hidden md:block" style={{ flexShrink: 0, marginLeft: 12 }}>
            <motion.div
              animate={{ opacity: isHovered ? 1 : 0.7 }}
              transition={{ duration: 0.3 }}
            >
              <OrbitalRing progress={ringProgress} color={ringColor} size={52} animated={isHovered} overLimit={isOverLimit} />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
